import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';

import { useAuth } from '@/contexts/auth-context';
import { UserRole } from '@/api-models/UserRole';
import { useAdminUsers, updateUserRole, type AdminUser } from '@/api-models/admin/users';
import { formatAge, formatDate } from '@/features/admin/format';
import { useDebouncedValue } from '@/features/admin/hooks/useDebouncedValue';
import { Card } from '@/ui/Card';
import { Input } from '@/ui/Input';
import { Pagination } from '@/ui/Pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/Select';
import { SpinnerBlock } from '@/ui/Spinner';
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from '@/ui/Table';

const PAGE_SIZE = 25;
const ROLE_OPTIONS: UserRole[] = [UserRole.Admin, UserRole.Editor, UserRole.Viewer];

const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.Admin]: 'Admin',
  [UserRole.Editor]: 'Editor',
  [UserRole.Viewer]: 'Viewer',
};

interface UserRowProps {
  user: AdminUser;
  isSelf: boolean;
  onRoleChange: (userId: string, role: UserRole) => Promise<void>;
}

function UserRow({ user, isSelf, onRoleChange }: UserRowProps) {
  const [pendingRole, setPendingRole] = useState<string | null>(null);

  async function handleChange(newRole: UserRole) {
    setPendingRole(newRole);
    try {
      await onRoleChange(user.id, newRole);
    } finally {
      setPendingRole(null);
    }
  }

  const role = pendingRole ?? user.role;

  return (
    <TableRow>
      <TableCell>
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-fg">{user.name || user.email}</p>
          <p className="truncate text-2xs text-fg-muted">{user.email}</p>
        </div>
      </TableCell>
      <TableCell>
        <Select
          value={role}
          onValueChange={(v) => handleChange(v as UserRole)}
          disabled={isSelf || pendingRole !== null}
        >
          <SelectTrigger className="h-8 w-32" title={isSelf ? "You can't change your own role" : undefined}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ROLE_OPTIONS.map((r) => (
              <SelectItem key={r} value={r}>
                {ROLE_LABELS[r]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="whitespace-nowrap text-2xs text-fg-muted tabular-nums">
        {formatDate(user.created_at)}
      </TableCell>
      <TableCell className="whitespace-nowrap text-2xs text-fg-muted tabular-nums">
        {formatAge(user.last_seen_at)}
      </TableCell>
    </TableRow>
  );
}

export function AdminUsersPage() {
  const { me } = useAuth();
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebouncedValue(searchInput, 250);

  // Reset to page 1 whenever the search term actually changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const query = useMemo(
    () => ({ search: debouncedSearch || undefined, page, pageSize: PAGE_SIZE }),
    [debouncedSearch, page],
  );

  const { users, total, mutate, isLoading } = useAdminUsers(query);

  async function handleRoleChange(userId: string, role: UserRole) {
    try {
      await updateUserRole(userId, role);
      await mutate();
      toast.success('Role updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update role');
      throw err; // let row revert pending state
    }
  }

  return (
    <>
      <header>
        <h1 className="page-title">Users</h1>
        <p className="page-subtitle">Every user in the platform. Change roles inline.</p>
      </header>

      <div className="flex flex-col gap-4">
        <Input
          inputSize={8}
          className="max-w-sm"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by email…"
          iconLeft={<MagnifyingGlassIcon size={14} />}
        />

        <Card padding="none" radius="2xl" className="overflow-hidden">
          {isLoading && users.length === 0 ? (
            <div className="py-12">
              <SpinnerBlock message="Loading users…" />
            </div>
          ) : users.length === 0 ? (
            <p className="p-8 text-center text-xs text-fg-muted">No users match.</p>
          ) : (
            <Table>
              <TableHead>
                <tr>
                  <TableHeaderCell>User</TableHeaderCell>
                  <TableHeaderCell className="w-36">Role</TableHeaderCell>
                  <TableHeaderCell className="w-36">Created</TableHeaderCell>
                  <TableHeaderCell className="w-30">Last seen</TableHeaderCell>
                </tr>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <UserRow key={u.id} user={u} isSelf={u.id === me?.id} onRoleChange={handleRoleChange} />
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
      </div>
    </>
  );
}
